// O2 — Flex parcel request detail screen.
// Rendered by OrderDetailScreen when order.direction === "flex".
// Pulls the *full* record from GET /parcel-requests/:id (returns the raw Order
// row with all flex* fields + statusLog). Falls back to the row we got from
// the list query while loading.

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api.js";
import { Icon, TgHeader } from "../components/ui/index.js";
import {
  formatDateShort,
  type FlexClientOrder,
} from "@mnp/shared";
import {
  FlexStatusPill,
  PackagingBadge,
  ORoSection,
  ORoRow,
  preorderMissingLabel,
  OrderCardSkeleton,
} from "../components/flex-form/OrderAtoms.js";
import { flagFor, flexCountry } from "../components/flex-form/FlexFormAtoms.js";
import { AuthImg } from "../components/flex-form/useAuthImage.js";

const API_BASE = import.meta.env.VITE_API_BASE || "";

type AttachmentRow = {
  id: string;
  type: "photo" | "note";
  description?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  sizeBytes?: number | null;
  createdAt: string;
};

// Raw Order shape we get from /parcel-requests/:id — это wire-тип
// serializeFlexOrderAsClient из @mnp/shared (FlexClientOrder, карточка
// flex-wire-contract-shared). Локального рукописного зеркала больше нет;
// алиас FlexOrderRow сохранён ради OrderDetailScreen (PR1 волны 2).
export type FlexOrderRow = FlexClientOrder;

// ─── Helpers ───────────────────────────────────────────────────
function joinAddrStruct(parts: Array<string | null | undefined>): string {
  return parts.filter((p) => p && String(p).trim()).join(", ");
}

function fullPhone(code?: string | null, local?: string | null): string | null {
  if (!code && !local) return null;
  return [code, local].filter(Boolean).join(" ").trim() || null;
}

// Country name + flag, with a small graceful fallback.
function countryLabel(code?: string | null): string {
  if (!code) return "—";
  const c = flexCountry(code);
  return c ? `${c.flag} ${c.name}` : `${flagFor(code)} ${code}`;
}

// ─── Component ─────────────────────────────────────────────────
export function FlexOrderDetail({ fallback }: { fallback?: FlexOrderRow } = {}) {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["parcel-request", id],
    queryFn: () => api<FlexOrderRow>(`/parcel-requests/${id}`),
    enabled: !!id,
    // placeholderData (не initialData) — fallback показывается до fetch'а,
    // но не оседает в cache, поэтому полный ответ /parcel-requests/:id
    // надёжно заменит обрезанные поля от /orders/:id serializer'a.
    placeholderData: fallback,
    staleTime: 0,
  });

  // Прикрепления (фото + заметки). Append-only, добавляются через
  // /orders/:id/attachments.
  const attachmentsQ = useQuery({
    queryKey: ["parcel-attachments", id],
    queryFn: () => api<AttachmentRow[]>(`/parcel-requests/${id}/attachments`),
    enabled: !!id,
    staleTime: 30 * 1000,
  });

  if (!id) return null;

  if (isLoading && !data) {
    return (
      <div className="app-shell">
        <TgHeader title="Заявка" onBack={() => nav("/orders")} />
        <div className="screen pad">
          <OrderCardSkeleton />
          <OrderCardSkeleton />
          <OrderCardSkeleton />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="app-shell">
        <TgHeader title="Заявка" onBack={() => nav("/orders")} />
        <div className="screen pad">
          <p className="t-body">Заявка не найдена</p>
          <button className="btn btn-secondary" onClick={() => nav("/orders")} style={{ marginTop: 12 }}>
            К списку заказов
          </button>
        </div>
      </div>
    );
  }

  const o = data;
  const isPreOrder = o.status === "pending_recipient_data";

  const title = o.flexRequestNumber ? `Заявка ${o.flexRequestNumber}` : "Заявка";

  // Timeline — 4 fixed steps. Each has ISO timestamp from server or null.
  const timeline: Array<{ key: string; label: string; date: string | null }> = [
    { key: "created", label: "Создано", date: o.createdAt ?? null },
    { key: "wh", label: "На складе", date: o.arrivedAt ?? null },
    { key: "transit", label: "В пути", date: o.flexShippedAt ?? null },
    { key: "done", label: "Доставлено", date: o.flexDeliveredAt ?? null },
  ];

  // Sender block — GE uses a free-form addressLine; other origins fill the
  // structured fields. Both forms share render.
  const senderAddr =
    o.flexSenderAddressLine ||
    joinAddrStruct([
      o.flexSenderAddrStreet,
      o.flexSenderAddrBuilding,
      o.flexSenderAddrApartment,
      o.flexSenderAddrCity,
      o.flexSenderAddrZip,
    ]);

  const senderPhone = fullPhone(o.flexSenderPhoneCode, o.flexSenderPhoneLocal);

  // Recipient — may be partly missing in pre-order state.
  const recipientPhone = fullPhone(o.flexRecipientPhoneCode, o.flexRecipientPhoneLocal);
  const recipientAddr = joinAddrStruct([
    o.flexRecipientStreet,
    o.flexRecipientBuilding,
    o.flexRecipientApt,
    o.flexRecipientCity,
    o.flexRecipientZip,
  ]);
  const recipientCityOnly = o.flexRecipientCity ?? null;

  // Pickup block — selfDeliver short-circuits everything.
  const pickupSlot = [o.flexPickupDay, o.flexPickupSlot].filter(Boolean).join(", ");
  const pickupPhone = fullPhone(o.flexSenderPhoneCode, o.flexPickupPhoneLocal);

  // CTAs
  const onContactManager = () => {
    // @marknpost — это менеджер (юзер), а не клиентский бот @mnpclientbot.
    const link = "https://t.me/marknpost";
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openTelegramLink) tg.openTelegramLink(link);
    else window.open(link, "_blank", "noopener,noreferrer");
  };
  const onRepeat = () => {
    // Префилл через location.state — FlexParcelFormScreen читает
    // location.state.flexPrefill.sourceOrder на mount и заполняет форму.
    nav("/new-parcel", { state: { flexPrefill: { sourceOrder: o } } });
  };
  // Review-цикл (2026-05-29): «Редактировать и отправить повторно» из rejected.
  // resubmit-id передаём через URL (?resubmit=<id>), а НЕ через location.state:
  // state теряется при холодном старте/перезаходе Mini App, и форма тогда
  // сваливалась в создание новой заявки (POST) вместо PATCH resubmit — юзер
  // получал пустую форму и дубль заказа. Префилл форма подтянет по этому id
  // сама (fetch /parcel-requests/:id). sourceOrder в state оставляем как
  // быстрый префилл, когда переход без reload.
  const onResubmit = () => {
    nav(`/new-parcel?resubmit=${encodeURIComponent(o.id)}`, {
      state: { flexPrefill: { sourceOrder: o } },
    });
  };
  const onFillRecipient = () => nav(`/orders/${o.id}/edit-recipient`);

  const isRejected = o.status === "rejected";

  return (
    <div className="app-shell">
      <TgHeader title={title} onBack={() => nav("/orders")} />
      <div className="screen pad" style={{ paddingBottom: 110, paddingTop: 14 }}>
        {/* Pre-order red banner */}
        {isPreOrder && (
          <div
            style={{
              background: "#FDECED",
              border: "1px solid #F5B5B7",
              borderLeft: "3px solid var(--mnp-red)",
              borderRadius: 10,
              padding: "10px 12px",
              marginBottom: 14,
              display: "flex",
              gap: 10,
            }}
          >
            <Icon name="alertCircle" size={18} color="var(--mnp-red-dark)" stroke={2} />
            <span style={{ flex: 1, fontSize: 13, lineHeight: 1.4, color: "var(--mnp-red-dark)" }}>
              <b style={{ fontWeight: 700, display: "block", marginBottom: 2 }}>
                У этой заявки не все данные
              </b>
              {preorderMissingLabel({
                missingPhone: o.flexRecipientMissingPhone,
                missingAddress: o.flexRecipientMissingAddress,
              })}{" "}
              — иначе включится платное хранение
            </span>
          </div>
        )}

        {/* Review-цикл (2026-05-29): красная плашка с причиной отказа +
            кнопка «Редактировать и отправить повторно». Появляется в
            status === "rejected"; после resubmit статус снова submitted. */}
        {isRejected && (
          <div
            data-testid="flex-rejected-banner"
            style={{
              background: "#FDECED",
              border: "1px solid #F5B5B7",
              borderLeft: "3px solid var(--mnp-red)",
              borderRadius: 10,
              padding: "12px 14px",
              marginBottom: 14,
            }}
          >
            <div style={{ display: "flex", gap: 10 }}>
              <Icon name="alertCircle" size={18} color="var(--mnp-red-dark)" stroke={2} />
              <span style={{ flex: 1, fontSize: 13, lineHeight: 1.5, color: "var(--mnp-red-dark)" }}>
                <b style={{ fontWeight: 700, display: "block", marginBottom: 2 }}>
                  Заявка отклонена
                </b>
                {o.flexRejectedReason ? (
                  <span style={{ whiteSpace: "pre-wrap" }}>{o.flexRejectedReason}</span>
                ) : (
                  <span>Менеджер не указал причину. Свяжитесь с поддержкой.</span>
                )}
              </span>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              data-testid="flex-resubmit-cta"
              onClick={onResubmit}
              style={{ marginTop: 12, width: "100%" }}
            >
              Редактировать и отправить повторно
            </button>
          </div>
        )}

        {/* Header row — route flags + status pill */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontFamily: "var(--mnp-font-display)",
              fontWeight: 800,
              fontSize: 22,
            }}
          >
            <span>{flagFor(o.flexOriginCountry ?? "")}</span>
            <Icon name="arrowRight" size={16} color="var(--mnp-teal-600)" />
            <span>{flagFor(o.flexDestinationCountry ?? "")}</span>
            {o.flexHadPackaging && (
              <span style={{ marginLeft: 8 }}>
                <PackagingBadge />
              </span>
            )}
          </div>
          <FlexStatusPill status={o.status} />
        </div>

        {/* Timeline */}
        <div
          style={{
            background: "var(--mnp-bg-alt)",
            border: "1px solid var(--mnp-border-soft)",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 14,
          }}
        >
          <p
            className="t-eyebrow"
            style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--mnp-fg-3)" }}
          >
            История
          </p>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            {timeline.map((t, i) => {
              const done = !!t.date;
              return (
                <div key={t.key} style={{ display: "contents" }}>
                  <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        margin: "0 auto 6px",
                        background: done ? "var(--mnp-teal-600)" : "var(--mnp-gray-200)",
                        border: done ? 0 : "2px solid var(--mnp-gray-300)",
                        display: "grid",
                        placeItems: "center",
                        color: "#fff",
                      }}
                    >
                      {done && <Icon name="check" size={12} stroke={3} />}
                    </div>
                    <p
                      style={{
                        margin: 0,
                        fontSize: 11,
                        fontWeight: 600,
                        color: done ? "var(--mnp-fg-1)" : "var(--mnp-fg-3)",
                      }}
                    >
                      {t.label}
                    </p>
                    <p
                      style={{
                        margin: "2px 0 0",
                        fontSize: 10,
                        color: "var(--mnp-fg-3)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {t.date ? formatDateShort(t.date) : "—"}
                    </p>
                  </div>
                  {i < timeline.length - 1 && (
                    <div
                      style={{
                        flex: 0.5,
                        height: 2,
                        marginTop: 10,
                        background: timeline[i + 1]?.date
                          ? "var(--mnp-teal-600)"
                          : "var(--mnp-gray-200)",
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Pre-order big CTA */}
        {isPreOrder && (
          <button
            type="button"
            onClick={onFillRecipient}
            style={{
              width: "100%",
              background: "#E37511",
              color: "#fff",
              border: 0,
              padding: "14px 16px",
              borderRadius: 12,
              fontFamily: "inherit",
              fontWeight: 700,
              fontSize: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              cursor: "pointer",
              marginBottom: 16,
              boxShadow: "0 6px 16px -6px rgba(227,117,17,.5)",
            }}
          >
            <Icon name="alertCircle" size={16} />
            Заполнить оставшиеся данные
          </button>
        )}

        {/* Route */}
        <ORoSection title="Маршрут">
          <ORoRow k="Откуда" v={countryLabel(o.flexOriginCountry)} />
          <ORoRow k="Куда" v={countryLabel(o.flexDestinationCountry)} />
        </ORoSection>

        {/* Sender */}
        <ORoSection title="Отправитель">
          <ORoRow k="Имя" v={o.flexSenderName ?? "—"} />
          <ORoRow k="Телефон" v={senderPhone ?? "—"} />
          <ORoRow k="Паспорт" v={o.flexSenderPassport ?? "—"} mono />
          {senderAddr && <ORoRow k="Адрес" v={senderAddr} />}
        </ORoSection>

        {/* Recipient — pre-order may have gaps */}
        <ORoSection title="Получатель">
          <ORoRow k="Имя" v={o.flexRecipientName ?? "— (не указано)"} muted={!o.flexRecipientName} />
          <ORoRow
            k="Телефон"
            v={recipientPhone ?? "— (не указано)"}
            muted={!recipientPhone}
          />
          {o.flexRecipientMessenger && (
            <ORoRow k="Мессенджер" v={o.flexRecipientMessenger} />
          )}
          <ORoRow
            k="Адрес"
            v={recipientAddr || recipientCityOnly || "— (не указано)"}
            muted={!recipientAddr && !recipientCityOnly}
          />
          {o.flexRecipientEmail && <ORoRow k="Email" v={o.flexRecipientEmail} />}
          {o.flexRecipientGeoUrl && (
            <ORoRow k="📍 Локация" v="Открыть на карте" link={o.flexRecipientGeoUrl} />
          )}
        </ORoSection>

        {/* Parameters */}
        <ORoSection title="Параметры">
          <ORoRow k="Мест" v={o.flexPieces != null ? String(o.flexPieces) : "—"} />
          <ORoRow
            k="Объявл. стоимость"
            v={o.flexDeclaredPriceEur != null ? `${Number(o.flexDeclaredPriceEur)} EUR` : "—"}
          />
          {o.flexCrateNeeded && o.flexCrateCount ? (
            <ORoRow k="Обрешётка" v={`${o.flexCrateCount} шт.`} />
          ) : null}
          {o.flexHadPackaging && <ORoRow k="Упаковка" v="была" />}
        </ORoSection>

        {/* Предварительная стоимость — ВСЕ числа считает сервер
            (buildFlexClientEstimate, живой NBG-курс). Клиент только рендерит.
            Карточка flex-pricing-single-owner. */}
        {(() => {
          const pickupGel = o.flexPickupFeeGel;          // этап «а»: известный забор
          const customsEst = o.flexCustomsEstimateGel;   // справочно «уточнит менеджер»
          const storage = o.flexStorageMonthlyGel;       // месячное хранение
          const prelim = o.flexPreliminaryTotalGel;      // этап «б»: предоплата
          const prelimFinal = o.flexPreliminaryTotalKind === "final";
          const anything =
            pickupGel != null || customsEst != null || storage != null || prelim != null;
          if (!anything) return null;
          return (
            <ORoSection title={prelimFinal ? "Стоимость" : "Предварительная стоимость"}>
              {prelim != null && (
                <ORoRow
                  k={prelimFinal ? "Итого" : "Предварительно"}
                  v={`${prelim} GEL`}
                />
              )}
              {pickupGel != null && (
                <ORoRow k="Забор" v={`${pickupGel} GEL`} />
              )}
              {customsEst != null && (
                <ORoRow k="Растаможка (≈ 18%)" v={`≈ ${customsEst} GEL · уточнит менеджер`} />
              )}
              {storage != null && (
                <ORoRow k="Хранение в мес." v={`${storage} GEL`} />
              )}
              {!prelimFinal && (
                <div style={{
                  marginTop: 8, padding: 10, borderRadius: 8,
                  background: "var(--mnp-bg-brand-soft, rgba(44,164,198,.10))",
                  fontSize: 12, color: "var(--mnp-fg-2)", lineHeight: 1.45,
                  display: "flex", gap: 6, alignItems: "flex-start",
                }}>
                  <Icon name="infoCircle" size={14} />
                  <span>
                    {prelim != null
                      ? <>Это <b>предварительная сумма</b>. Итог посчитает менеджер
                          после взвешивания: выше — доплата, ниже — вернём переплату.</>
                      : <>Точную сумму посчитает <b>менеджер</b> при приёмке посылки на
                          складе — тариф зависит от веса и габаритов.</>}
                  </span>
                </div>
              )}
            </ORoSection>
          );
        })()}

        {/* Pickup */}
        <ORoSection title="Забор">
          {o.flexSelfDeliver ? (
            <ORoRow k="Кто привозит" v="Привезу сам" />
          ) : (
            <>
              <ORoRow k="Город" v={o.flexPickupCity ?? "—"} />
              <ORoRow
                k="Адрес"
                v={o.flexPickupAddress ?? "—"}
                link={o.flexPickupGeoUrl ?? undefined}
              />
              {o.flexPickupFloor != null && <ORoRow k="Этаж" v={String(o.flexPickupFloor)} />}
              {o.flexPickupHasElevator != null && (
                <ORoRow k="Лифт" v={o.flexPickupHasElevator ? "есть" : "нет"} />
              )}
              {pickupPhone && <ORoRow k="Телефон" v={pickupPhone} />}
              {pickupSlot && <ORoRow k="Слот" v={pickupSlot} />}
            </>
          )}
        </ORoSection>

        {/* Storage */}
        <ORoSection title="Хранение">
          {o.flexStorageNeeded ? (
            <ORoRow
              k="Нужно"
              v={o.flexStorageUntil ? `До ${formatDateShort(o.flexStorageUntil)}` : "да"}
            />
          ) : (
            <ORoRow k="Нужно" v="Не запрошено" muted />
          )}
        </ORoSection>

        {/* Attachments — фото + текстовые ноты, append-only.
            См. POST /parcel-requests/:id/attachments + FlexAttachmentsScreen. */}
        <ORoSection title="📸 Содержимое посылки">
          {(() => {
            const list = attachmentsQ.data ?? [];
            const photos = list.filter((a) => a.type === "photo");
            const notes = list.filter((a) => a.type === "note" && a.description);
            const empty = !attachmentsQ.isLoading && photos.length === 0 && notes.length === 0;
            return (
              <div>
                {empty && (
                  <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--mnp-fg-3)" }}>
                    Вы ещё не прикрепили фото/описание.
                  </p>
                )}
                {photos.length > 0 && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
                      gap: 8,
                      marginBottom: notes.length > 0 ? 12 : 10,
                    }}
                  >
                    {photos.map((att) => (
                      <AuthImg
                        key={att.id}
                        url={`${API_BASE}/api/v1/parcel-requests/${o.id}/attachments/${att.id}/file`}
                        width={120}
                        height={120}
                        alt={att.fileName ?? "фото"}
                        style={{
                          width: "100%",
                          aspectRatio: "1 / 1",
                          height: "auto",
                          objectFit: "cover",
                          borderRadius: 8,
                          display: "block",
                        }}
                      />
                    ))}
                  </div>
                )}
                {notes.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    {notes.map((n) => (
                      <p
                        key={n.id}
                        style={{
                          margin: "0 0 6px",
                          fontSize: 13,
                          lineHeight: 1.45,
                          color: "var(--mnp-fg-1)",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {n.description}
                      </p>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => nav(`/orders/${o.id}/attachments`)}
                  style={{
                    width: "100%",
                    background: "var(--mnp-teal-600)",
                    color: "#fff",
                    border: 0,
                    padding: "12px 16px",
                    borderRadius: 10,
                    fontFamily: "inherit",
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <Icon name="plus" size={14} stroke={2} />
                  Добавить фото или описание
                </button>
              </div>
            );
          })()}
        </ORoSection>

        {/* TODO(cancelled-state): if status === "cancelled" we should hide the
            «Повторить» CTA's primary tone (or even disable it for in_transit /
            delivered to avoid duplicate shipments). Punt for prod-start —
            menu agent will tune copy in M5. */}
      </div>

      {/* Sticky footer */}
      <div
        className="ff-cta"
        style={{
          display: "flex",
          gap: 8,
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onContactManager}>
          <Icon name="bell" size={14} /> Менеджер
        </button>
        <button type="button" className="btn btn-primary" style={{ flex: 1 }} onClick={onRepeat}>
          Повторить
        </button>
      </div>
    </div>
  );
}
